import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User,
  Video,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/Layout/DashboardLayout';

const TerminePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/');
      return;
    }
    
    setUser(JSON.parse(userData));
  }, [navigate]);

  // Mock appointments data
  const appointments = [
    { 
      id: 1, 
      title: 'Besichtigung - Mustermann', 
      time: '09:00 - 10:00',
      type: 'vor_ort',
      location: 'Musterstraße 123, Köln',
      kunde: 'Max Mustermann'
    },
    { 
      id: 2, 
      title: 'Video-Call - Versicherung Allianz', 
      time: '11:00 - 11:30',
      type: 'video',
      location: 'Online',
      kunde: 'Allianz Versicherung'
    },
    { 
      id: 3, 
      title: 'Telefonat - Schmidt', 
      time: '14:00 - 14:30',
      type: 'telefon',
      location: 'Telefon',
      kunde: 'Erika Schmidt'
    },
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4 text-blue-500" />;
      case 'telefon': return <Phone className="w-4 h-4 text-green-500" />;
      default: return <MapPin className="w-4 h-4 text-amber-500" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video': return 'border-l-blue-500 bg-blue-50';
      case 'telefon': return 'border-l-green-500 bg-green-50';
      default: return 'border-l-amber-500 bg-amber-50';
    }
  };

  const formatMonth = (date) => {
    return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Generate calendar days
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Adjust for Monday start
  
  const days = [];
  for (let i = 0; i < adjustedFirstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <DashboardLayout user={user}>
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Termine</h1>
            <p className="text-gray-500 text-sm">Terminplanung und Kalender</p>
          </div>
          <Button className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] text-white gap-2">
            <Plus className="w-4 h-4" />
            Neuer Termin
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 capitalize">{formatMonth(currentDate)}</h2>
              <div className="flex items-center gap-2">
                <button 
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <button 
                  onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  Heute
                </button>
                <button 
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
              {days.map((day, index) => {
                const isToday = day === new Date().getDate() && 
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear();
                
                return (
                  <div 
                    key={index} 
                    className={`aspect-square flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                      day 
                        ? isToday 
                          ? 'bg-gradient-to-br from-[#29beff] to-[#5dd3ff] text-white font-semibold' 
                          : 'hover:bg-gray-100 text-gray-900'
                        : ''
                    }`}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Today's Appointments */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-gray-400" />
              Heutige Termine
            </h2>
            
            <div className="space-y-3">
              {appointments.map((apt) => (
                <div 
                  key={apt.id} 
                  className={`p-3 rounded-lg border-l-4 ${getTypeColor(apt.type)}`}
                >
                  <div className="flex items-start justify-between">
                    <h3 className="font-medium text-gray-900 text-sm">{apt.title}</h3>
                    {getTypeIcon(apt.type)}
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {apt.time}
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {apt.location}
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {apt.kunde}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {appointments.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Keine Termine heute</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TerminePage;
